import { useState, useEffect, useCallback, useRef } from 'react';
import { useWatchdog } from './useWatchdog';
import type { Midia } from '@/shared/types';

export interface UseSlideshowAdvanceParams {
  activeMidias:    Midia[];
  momentoEspecial: { nome: string } | null;
  hasPlaylist:     boolean;
  videoRef:        React.RefObject<HTMLVideoElement | null>;
  telaId:          string;
}

export interface UseSlideshowAdvanceReturn {
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  advanceSlide(): void;
  slideStartTimeRef: React.RefObject<number>;
}

/** Avanço automático do slideshow de mídias locais (timer por duração de
 *  cada mídia) + supervisão do Watchdog (vídeo/imagem congelados). */
export function useSlideshowAdvance({
  activeMidias, momentoEspecial, hasPlaylist, videoRef, telaId,
}: UseSlideshowAdvanceParams): UseSlideshowAdvanceReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slideStartTimeRef = useRef<number>(Date.now());
  useEffect(() => { slideStartTimeRef.current = Date.now(); }, [currentIndex]);

  useEffect(() => {
    if (activeMidias.length > 0 && currentIndex >= activeMidias.length) setCurrentIndex(0);
  }, [activeMidias.length, currentIndex]);

  const advanceSlide = useCallback(() => {
    if (activeMidias.length === 0) return;
    setCurrentIndex(prev => (prev + 1) % activeMidias.length);
  }, [activeMidias.length]);

  // Com 1 mídia não há para onde avançar: advanceSlide seria um no-op
  // (0 → 0) e o timer só serviria para o Watchdog ver uma imagem "presa".
  useEffect(() => {
    if (activeMidias.length <= 1 || momentoEspecial) return;
    const current = activeMidias[currentIndex];
    if (!current) return;
    const timer = setTimeout(advanceSlide, current.duracao_segundos * 1_000);
    return () => clearTimeout(timer);
  }, [currentIndex, activeMidias, advanceSlide, momentoEspecial]);

  // Imagem única fica parada por definição — o Watchdog não deve tratá-la como
  // image_stuck (advance no-op → reload em loop). Vídeo único continua vigiado
  // (loop no <video>; pausa/frame congelado ainda são falhas reais).
  const midiaAtual = activeMidias[currentIndex] ?? activeMidias[0] ?? null;
  const imagemUnica = activeMidias.length === 1 && midiaAtual?.tipo !== 'video';
  const currentMidiaForWatchdog = (momentoEspecial || hasPlaylist || imagemUnica)
    ? null
    : midiaAtual;

  useWatchdog({
    currentMidia: currentMidiaForWatchdog,
    videoRef,
    slideStartTimeRef,
    onAdvance: advanceSlide,
    telaId,
  });

  return { currentIndex, setCurrentIndex, advanceSlide, slideStartTimeRef };
}
